/**
 * Product Upsell Handlers
 * Global functions for handling product upsell add to cart and variant selection
 */

// Handle variant selection change
window.handleProductUpsellVariantChange = function (selectElement, formId, skipImage) {
  const form = document.getElementById(formId);
  if (!form) {
    console.error("Form not found:", formId);
    return;
  }

  const productJsonEl = document.getElementById("ProductJson-" + formId);
  if (!productJsonEl) {
    console.error("Product JSON not found for form:", formId);
    return;
  }

  try {
    const product = JSON.parse(productJsonEl.textContent);
    const selects = form.querySelectorAll('select[name^="options["]');
    const selectedOptions = Array.from(selects).map((s) => s.value);

    // Find matching variant
    const variant = product.variants.find((v) => selectedOptions.every((opt, i) => v.options[i] === opt));

    if (variant) {
      const hiddenInput = form.querySelector('input[name="id"]');
      if (hiddenInput) {
        hiddenInput.value = variant.id;
      }

      // Update all price displays (button, toggle, and standalone)
      if (variant.price != null) {
        const currency = document.querySelector('meta[name="currency"]')?.content || "USD";
        const formattedPrice = formatMoney(variant.price, currency);
        const container = form.closest('[class*="upsell-product-container-"]');
        if (container) {
          container.querySelectorAll('.upsell-price, [class*="upsell-toggle-price-"], [class*="upsell-product-price-"]').forEach(function (el) {
            el.textContent = formattedPrice;
          });
        }
      }
      

      // Update product image to match selected variant
      var img = skipImage ? null : form.querySelector("img");
      if (img) {
        var imgData = variant && variant.featured_image ? variant.featured_image : null;
        if (!imgData && product.featured_image) {
          // Fallback to product featured image
          var fi = product.featured_image;
          imgData = { src: typeof fi === "string" ? fi : fi.src, alt: "" };
        }
        if (imgData && imgData.src) {
          var newSrc = imgData.src.replace(/^\/\//, "https://").split("?")[0] + "?width=150";
          if (newSrc !== img.src) {
            var preload = new Image();
            preload.onload = function () {
              img.src = newSrc;
              if (imgData.alt) img.alt = imgData.alt;
            };
            preload.src = newSrc;
          }
        }
      }
    }

    // Disable unavailable variant option combinations
    selects.forEach(function (currentSelect, idx) {
      Array.from(currentSelect.options).forEach(function (opt) {
        var test = selectedOptions.slice();
        test[idx] = opt.value;
        opt.disabled = !product.variants.some(function (v) {
          return test.every(function (val, i) {
            return v.options[i] === val;
          });
        });
      });
    });
  } catch (error) {
    console.error("Error handling variant change:", error);
  }
};

function getCartMutationCoordinator() {
  return window.AifufandCartMutation || null;
}

function getUpsellCartSections(cartDrawer) {
  return cartDrawer && typeof cartDrawer.getSectionsToRender === "function"
    ? cartDrawer.getSectionsToRender().map((section) => section.id)
    : ["cart-drawer", "cart-icon-bubble"];
}

async function parseUpsellCartResponse(response, fallbackMessage) {
  const text = await response.text();
  const cfMitigated = response.headers.get("cf-mitigated");
  const isCloudflareChallenge = cfMitigated?.toLowerCase() === "challenge";
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch (parseError) {
    const error = new Error(
      isCloudflareChallenge
        ? "Your connection needs verification. Please complete the verification and try again."
        : response.status === 429
          ? "Your cart is updating. Please wait a moment and try again."
          : fallbackMessage,
    );
    error.status = response.status;
    error.retryAfter = response.headers.get("Retry-After");
    error.cfMitigated = cfMitigated;
    throw error;
  }

  if (!response.ok || data.status >= 400 || data.description) {
    const error = new Error(data.description || data.message || fallbackMessage);
    error.status = data.status || response.status;
    error.retryAfter = response.headers.get("Retry-After");
    error.cfMitigated = cfMitigated;
    throw error;
  }
  return data;
}

async function renderUpsellCart(cartDrawer, data, variantId) {
  const renderData = {
    sections: data.sections,
    id: variantId,
  };
  // Never fabricate an item count of 0: that can make a non-empty drawer
  // render as empty when Shopify returns only bundled sections.
  if (typeof data.item_count === "number") renderData.item_count = data.item_count;

  if (cartDrawer && data.sections && typeof cartDrawer.renderContents === "function") {
    cartDrawer.renderContents(renderData);
  } else if (window.refreshCartDrawer) {
    await window.refreshCartDrawer(data.sections ? renderData : null);
  } else if (cartDrawer) {
    const cartUrl = window.routes?.cart_url || "/cart";
    const response = await fetch(`${cartUrl}?section_id=cart-drawer`);
    if (!response.ok) throw new Error("Unable to refresh cart drawer");
    const doc = new DOMParser().parseFromString(await response.text(), "text/html");
    const newCartDrawer = doc.querySelector("cart-drawer");
    if (newCartDrawer) {
      cartDrawer.innerHTML = newCartDrawer.innerHTML;
      if (typeof upsellCarousel === "function") upsellCarousel();
    }
  }
}

function resetUpsellButton(buttonElement, originalHTML, resultClass, label) {
  buttonElement.classList.remove("is-loading");
  buttonElement.classList.add(resultClass);
  buttonElement.innerHTML = label;
  window.setTimeout(() => {
    if (!buttonElement.isConnected) return;
    buttonElement.classList.remove(resultClass);
    buttonElement.innerHTML = originalHTML;
    buttonElement.disabled = false;
    buttonElement.dataset.processing = "false";
  }, 2000);
}

// Handle add to cart. This deliberately does not emit a new pixel event: the
// existing Omega integration remains the sole analytics owner for AddToCart.
window.handleProductUpsellAdd = async function (buttonElement, formId) {
  if (buttonElement.dataset.processing === "true") return;

  const form = document.getElementById(formId);
  if (!form) {
    console.error("Form not found:", formId);
    return;
  }

  const variantId = form.querySelector('input[name="id"]')?.value;
  if (!variantId) {
    console.error("No variant ID found");
    return;
  }

  const coordinator = getCartMutationCoordinator();
  const mutation = coordinator?.reserve({ type: "upsell-add" });
  if (coordinator && !mutation) {
    const originalHTML = buttonElement.innerHTML;
    buttonElement.dataset.processing = "true";
    buttonElement.disabled = true;
    resetUpsellButton(buttonElement, originalHTML, "upsell-add-error", "Please wait");
    return;
  }

  const originalHTML = buttonElement.innerHTML;
  buttonElement.dataset.processing = "true";
  buttonElement.disabled = true;
  buttonElement.classList.add("is-loading");
  buttonElement.innerHTML = '<span class="button-spinner"></span>';

  let status;
  let retryAfter;
  let cfMitigated;
  try {
    if (mutation && !coordinator.start(mutation)) throw new Error("Cart mutation could not start");

    const cartDrawer = document.querySelector("cart-drawer");
    const response = await fetch((window.routes?.cart_add_url || "/cart/add") + ".js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: getUpsellCartSections(cartDrawer),
        sections_url: window.location.pathname,
      }),
    });
    status = response.status;
    retryAfter = response.headers.get("Retry-After");
    cfMitigated = response.headers.get("cf-mitigated");
    const data = await parseUpsellCartResponse(response, "Failed to add to cart");

    await renderUpsellCart(cartDrawer, data, variantId);
    resetUpsellButton(buttonElement, originalHTML, "upsell-add-success", "Added!");
  } catch (error) {
    status = error.status || status;
    retryAfter = error.retryAfter || retryAfter;
    cfMitigated = error.cfMitigated || cfMitigated;
    console.error("Error adding to cart:", error);
    resetUpsellButton(
      buttonElement,
      originalHTML,
      "upsell-add-error",
      cfMitigated?.toLowerCase() === "challenge" ? "Verify connection" : status === 429 ? "Please wait" : "Error",
    );
  } finally {
    if (mutation) coordinator.release(mutation, { status, retryAfter, cfMitigated });
  }
};

// Handle toggle switch add/remove from cart
window.handleProductUpsellToggle = async function (toggleElement, formId, uniqueId) {
  if (toggleElement.dataset.processing === "true") return;

  const coordinator = getCartMutationCoordinator();
  const mutation = coordinator?.reserve({ type: "upsell-toggle" });
  if (coordinator && !mutation) {
    toggleElement.checked = !toggleElement.checked;
    return;
  }
  toggleElement.dataset.processing = "true";

  const form = document.getElementById(formId);
  if (!form) {
    console.error("Form not found:", formId);
    toggleElement.checked = !toggleElement.checked; // Revert
    toggleElement.dataset.processing = "false";
    if (mutation) coordinator.cancel(mutation);
    return;
  }

  const variantId = form.querySelector('input[name="id"]')?.value;
  if (!variantId) {
    console.error("No variant ID found");
    toggleElement.checked = !toggleElement.checked; // Revert
    toggleElement.dataset.processing = "false";
    if (mutation) coordinator.cancel(mutation);
    return;
  }

  // Use uniqueId to find the toggle switch more reliably
  const toggleSwitch = document.querySelector(".upsell-toggle-switch-" + uniqueId) || toggleElement.parentElement;
  const isAdding = toggleElement.checked;

  const cartAddUrl = window.routes?.cart_add_url || "/cart/add";
  const cartChangeUrl = window.routes?.cart_change_url || "/cart/change";
  const cartDrawer = document.querySelector("cart-drawer");
  const sections = getUpsellCartSections(cartDrawer);

  // Show loading state
  if (toggleSwitch) {
    toggleSwitch.classList.add("is-loading");
  }

  let status;
  let retryAfter;
  try {
    if (mutation && !coordinator.start(mutation)) throw new Error("Cart mutation could not start");

    let data;
    if (isAdding) {
      const response = await fetch(cartAddUrl + ".js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      status = response.status;
      retryAfter = response.headers.get("Retry-After");
      data = await parseUpsellCartResponse(response, "Failed to add to cart");
    } else {
      // Resolve the current line key while the coordinator owns the cart.
      // Variant IDs are not stable line identifiers when properties, selling
      // plans, or discounts create duplicate variants.
      const cartResponse = await fetch((window.routes?.cart_url || "/cart") + ".js", {
        headers: { Accept: "application/json" },
      });
      if (!cartResponse.ok) {
        const error = new Error("Failed to read cart");
        error.status = cartResponse.status;
        error.retryAfter = cartResponse.headers.get("Retry-After");
        throw error;
      }
      const cart = await cartResponse.json();

      const matchingItems = cart.items.filter((item) => String(item.variant_id) === String(variantId));

      if (matchingItems.length === 1) {
        const cartItem = matchingItems[0];
        const newQuantity = cartItem.quantity - 1;
        const response = await fetch(cartChangeUrl + ".js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            id: cartItem.key,
            quantity: newQuantity,
            sections,
            sections_url: window.location.pathname,
          }),
        });
        status = response.status;
        retryAfter = response.headers.get("Retry-After");
        data = await parseUpsellCartResponse(response, "Failed to remove from cart");
      } else if (matchingItems.length === 0) {
        throw new Error("The selected item is no longer in the cart");
      } else {
        // A variant can occupy multiple Shopify lines when its properties or
        // selling plan differ. Without a unique key, refusing the toggle is
        // safer than removing an arbitrary customer-selected line.
        throw new Error("This item has multiple cart lines and cannot be removed from this toggle");
      }
    }

    await renderUpsellCart(cartDrawer, data, variantId);
    if (toggleSwitch) {
      toggleSwitch.classList.remove("is-loading");
    }
    toggleElement.dataset.processing = "false";
  } catch (error) {
    status = error.status || status;
    retryAfter = error.retryAfter || retryAfter;
    console.error("Error updating cart:", error);

    toggleElement.checked = !toggleElement.checked;
    if (toggleSwitch) {
      toggleSwitch.classList.remove("is-loading");
    }
    toggleElement.dataset.processing = "false";
  } finally {
    if (mutation) coordinator.release(mutation, { status, retryAfter });
  }
};

// Event delegation for toggle switches - more reliable than inline handlers
document.addEventListener("DOMContentLoaded", function () {
  document.addEventListener("change", function (e) {
    if (e.target.classList.contains("upsell-toggle-input")) {
      const formId = e.target.dataset.formId;
      const uniqueId = e.target.dataset.uniqueId;
      if (formId && uniqueId) {
        handleProductUpsellToggle(e.target, formId, uniqueId);
      }
    }
  });
});
// Helper function to format money
function formatMoney(cents, currency = "USD") {
  const amount = cents / 100;

  // Try to use Shopify's money format if available
  if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
    return Shopify.formatMoney(cents, Shopify.money_format || "{{amount}}");
  }

  // Fallback formatting
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(amount);
}
